import { toast, ExternalToast } from "sonner";

/**
 * Centralised toast helpers.
 *
 * Usage:
 *   import { showToast } from "@/lib/toast";
 *
 *   showToast.success("Saved!");
 *   showToast.error("Oops");
 *   showToast.apiError(error);               // error.message ?? generic fallback
 *   showToast.apiError(error, "Custom msg");  // error.message ?? custom fallback
 */

const DEFAULT_ERROR = "Something went wrong. Please try again.";

export const showToast = {
  /** Green success pill */
  success(message: string, opts?: ExternalToast) {
    toast.success(message, opts);
  },

  /** Red error pill */
  error(message: string, opts?: ExternalToast) {
    toast.error(message, opts);
  },

  /** Blue info pill */
  info(message: string, opts?: ExternalToast) {
    toast.info(message, opts);
  },

  /** Yellow warning pill */
  warning(message: string, opts?: ExternalToast) {
    toast.warning(message, opts);
  },

  /**
   * Extract message from a caught error and show an error toast.
   *
   * Handles `Error` objects, `AbortError` (timeouts), plain strings,
   * and objects with a `.message` property (API responses).
   *
   * @param err   - The caught error value
   * @param fallback - Fallback message if err has no usable message
   */
  apiError(err: unknown, fallback: string = DEFAULT_ERROR) {
    let message = fallback;

    if (err instanceof Error) {
      message = err.name === "AbortError" ? "Request timed out. Please try again." : err.message || fallback;
    } else if (typeof err === "string") {
      message = err;
    } else if (err && typeof err === "object" && "message" in err) {
      message = String((err as { message: unknown }).message) || fallback;
    }

    toast.error(message);
  },

  /** Dismiss a specific toast or all toasts */
  dismiss(id?: string | number) {
    toast.dismiss(id);
  },
};
